import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, LoadScript, Marker, OverlayView } from '@react-google-maps/api';
import RinkCard from './RinkCard';
import MapControls from './MapControls';
import './RinkMap.css';

const mapContainerStyle = {
  height: '100vh',
  width: '100%'
};

const center = {
  lat: 45.5017,
  lng: -73.5673
};

// Light winter theme map styling
const mapStyles = [
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [{ "color": "#a8d4f0" }]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.fill",
    "stylers": [{ "color": "#4d8fbe" }]
  },
  {
    "featureType": "landscape",
    "elementType": "geometry",
    "stylers": [{ "color": "#f5f9fc" }]
  },
  {
    "featureType": "landscape.natural",
    "elementType": "geometry",
    "stylers": [{ "color": "#e8f4ed" }]
  },
  {
    "featureType": "road",
    "elementType": "geometry",
    "stylers": [{ "color": "#ffffff" }]
  },
  {
    "featureType": "road",
    "elementType": "geometry.stroke",
    "stylers": [{ "color": "#d6e5f3" }]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry",
    "stylers": [{ "color": "#e0ecf7" }]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry.stroke",
    "stylers": [{ "color": "#c5d9ed" }]
  },
  {
    "featureType": "poi",
    "elementType": "labels.icon",
    "stylers": [
      { "visibility": "on" },
      { "color": "#a3c2c2" }
    ]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [
      { "visibility": "on" },
      { "color": "#94a3b8" },
      { "weight": 0.5 }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry",
    "stylers": [{ "visibility": "on" }, { "color": "#d4eede" }]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.fill",
    "stylers": [{ "visibility": "on" }, { "color": "#6b8a7a" }]
  },
  {
    "featureType": "transit",
    "elementType": "geometry",
    "stylers": [{ "color": "#dbe9f4" }]
  },
  {
    "featureType": "transit.station",
    "elementType": "labels.icon",
    "stylers": [
      { "visibility": "on" },
      { "color": "#0066cc" },
      { "weight": 4 }
    ]
  },
  {
    "featureType": "transit.station",
    "elementType": "labels.text.fill",
    "stylers": [
      { "visibility": "on" },
      { "color": "#004c99" },
      { "weight": 8 }
    ]
  },
  {
    "featureType": "administrative",
    "elementType": "labels.text.fill",
    "stylers": [{ "color": "#5c7d99" }]
  },
  {
    "featureType": "administrative.neighborhood",
    "elementType": "labels.text.fill",
    "stylers": [{ "color": "#7a9ab5" }]
  }
];

const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },

  // General POIs - Extremely muted
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#4f5869" }] // Darker, low contrast text
  },
  {
    featureType: "poi",
    elementType: "labels.icon",
    stylers: [
      { color: "#374151" }, // Almost blending with background
      { saturation: -100 },
      { lightness: -50 }
    ]
  },

  // Parks - Keep visible but subtle
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#263c3f" }, { visibility: "on" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b9a76" }],
  },

  // Transit - Subway Stations (High Visibility)
  {
    featureType: "transit.station",
    elementType: "labels.icon",
    stylers: [
      { visibility: "on" },
      { color: "#60a5fa" }, // Bright blue (matches our primary color)
      { weight: 4 } // Force prominence
    ]
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [
      { visibility: "on" },
      { color: "#93c5fd" }, // Light blue text for readability
      { weight: 8 }
    ]
  },

  // Roads - Standard dark mode
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca5b3" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }],
  },

  // Transit Lines - Subtle
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }],
  },

  // Water - Deep dark blue
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#515c6d" }],
  },
  {
    featureType: "water",
    stylers: [{ color: "#17263c" }],
  },
];


// Create SVG marker icons for skating rinks
const createRinkMarkerIcon = (isOpen, condition) => {
  const mainColor = isOpen ? '#22c55e' : '#ef4444';
  const bgColor = isOpen ? '#dcfce7' : '#fee2e2';
  const strokeColor = isOpen ? '#16a34a' : '#dc2626';

  // Add glow for excellent condition
  const glowFilter = condition === 'Excellente' ?
    `<filter id="glow"><feGaussianBlur stdDeviation="1.5" result="coloredBlur"/><feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>` : '';
  const filterAttr = condition === 'Excellente' ? 'filter="url(#glow)"' : '';

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
      <defs>
        ${glowFilter}
        <linearGradient id="grad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:${bgColor};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${mainColor};stop-opacity:0.3" />
        </linearGradient>
      </defs>
      <!-- Pin shape -->
      <path ${filterAttr} d="M16 0C8.27 0 2 6.27 2 14c0 10.5 14 25 14 25s14-14.5 14-25C30 6.27 23.73 0 16 0z"
            fill="url(#grad)" stroke="${strokeColor}" stroke-width="1.5"/>
      <!-- Inner circle -->
      <circle cx="16" cy="13" r="8" fill="white" stroke="${mainColor}" stroke-width="1.5"/>
      <!-- Snowflake icon -->
      <g transform="translate(16, 13)" fill="${mainColor}">
        <line x1="0" y1="-4" x2="0" y2="4" stroke="${mainColor}" stroke-width="1.5" stroke-linecap="round"/>
        <line x1="-3.5" y1="-2" x2="3.5" y2="2" stroke="${mainColor}" stroke-width="1.5" stroke-linecap="round"/>
        <line x1="-3.5" y1="2" x2="3.5" y2="-2" stroke="${mainColor}" stroke-width="1.5" stroke-linecap="round"/>
      </g>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Create SVG marker icons for ski trails
const createSkiMarkerIcon = (isOpen, condition) => {
  const mainColor = isOpen ? '#3b82f6' : '#ef4444'; // Blue for open ski trails
  const bgColor = isOpen ? '#dbeafe' : '#fee2e2';
  const strokeColor = isOpen ? '#2563eb' : '#dc2626';

  // Add glow for excellent condition
  const glowFilter = condition === 'Excellente' ?
    `<filter id="glow"><feGaussianBlur stdDeviation="1.5" result="coloredBlur"/><feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>` : '';
  const filterAttr = condition === 'Excellente' ? 'filter="url(#glow)"' : '';

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
      <defs>
        ${glowFilter}
        <linearGradient id="skigrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:${bgColor};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${mainColor};stop-opacity:0.3" />
        </linearGradient>
      </defs>
      <!-- Pin shape -->
      <path ${filterAttr} d="M16 0C8.27 0 2 6.27 2 14c0 10.5 14 25 14 25s14-14.5 14-25C30 6.27 23.73 0 16 0z"
            fill="url(#skigrad)" stroke="${strokeColor}" stroke-width="1.5"/>
      <!-- Inner circle -->
      <circle cx="16" cy="13" r="8" fill="white" stroke="${mainColor}" stroke-width="1.5"/>
      <!-- Pine tree icon -->
      <g transform="translate(16, 13)" fill="${mainColor}">
        <!-- Tree layers -->
        <polygon points="0,-5 -4,0 -2,0 -5,4 4,4 1,0 3,0" />
        <!-- Trunk -->
        <rect x="-1" y="4" width="2" height="2" />
      </g>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Legacy function for backward compatibility
const createMarkerIcon = (isOpen, condition) => createRinkMarkerIcon(isOpen, condition);

const createUserLocationIcon = () => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
      <defs>
        <filter id="blur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="1.5"/>
          <feOffset dx="0" dy="1" result="offsetblur"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.3"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <circle cx="15" cy="15" r="10" fill="white" filter="url(#blur)"/>
      <circle cx="15" cy="15" r="7" fill="#4285F4"/>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const RinkMap = ({ rinks, apiKey, theme, toggleTheme, viewType = 'rink', onViewTypeChange }) => {
  const [selectedRink, setSelectedRink] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [mapZoom, setMapZoom] = useState(12);
  const [mapCenter, setMapCenter] = useState(center);
  const [filter, setFilter] = useState('all');
  const [mapRef, setMapRef] = useState(null);

  const onMapLoad = useCallback((map) => {
    setMapRef(map);
    map.addListener('zoom_changed', () => {
      setMapZoom(map.getZoom());
    });
    setIsMapLoaded(true);
  }, []);

  // Adjust marker positions for overlapping markers
  const adjustMarkerPositions = (markers) => {
    // Group markers by location to determine overlap
    const groups = {};
    markers.forEach((marker, index) => {
      // Use high precision key
      const key = `${marker.location.lat},${marker.location.lng}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(index);
    });

    const adjustedMarkers = [...markers];

    Object.values(groups).forEach(groupIndices => {
      const count = groupIndices.length;
      if (count > 1) {
        // Distribute markers in a circle around the center point
        // 0.00020 degrees is roughly 20-25 meters, enough to see distinct pins at zoom 12-14
        const radius = 0.00020;
        const angleStep = (2 * Math.PI) / count;

        groupIndices.forEach((markerIndex, i) => {
          const angle = i * angleStep;
          // Standard rotation to creating a circular pattern
          // Adjusting lat/lng directly. For small distances at this latitude, this circular approx is sufficient.
          const latOffset = radius * Math.cos(angle);
          const lngOffset = radius * Math.sin(angle);

          adjustedMarkers[markerIndex] = {
            ...adjustedMarkers[markerIndex],
            location: {
              lat: adjustedMarkers[markerIndex].location.lat + latOffset,
              lng: adjustedMarkers[markerIndex].location.lng + lngOffset
            }
          };
        });
      }
    });

    return adjustedMarkers;
  };

  // Filter rinks based on current filter
  const getFilteredRinks = () => {
    if (filter === 'open') {
      return markers.filter(r => r.ouvert == 1);
    } else if (filter === 'closed') {
      return markers.filter(r => r.ouvert != 1);
    }
    return markers;
  };

  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);

  // Handle zoom
  const handleZoomIn = () => {
    if (mapRef) {
      mapRef.setZoom(mapRef.getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapRef) {
      mapRef.setZoom(mapRef.getZoom() - 1);
    }
  };

  // Handle locate user
  const handleLocateUser = () => {
    // If we already have a location, pan to it immediately for better UX
    if (userLocation && mapRef) {
      mapRef.panTo(userLocation);
      mapRef.setZoom(15);
    }

    setIsLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };

          setUserLocation(location);

          // Only pan if we moved significantly or it's the first locate
          // 0.0001 deg is approx 11 meters
          const isSignificantMove = !userLocation ||
            Math.abs(location.lat - userLocation.lat) > 0.0001 ||
            Math.abs(location.lng - userLocation.lng) > 0.0001;

          if (mapRef && isSignificantMove) {
            mapRef.panTo(location);
            mapRef.setZoom(15);
          }
          setIsLocating(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          if (!userLocation) {
            alert('Impossible d\'obtenir votre position. Veuillez activer la géolocalisation.');
          }
          setIsLocating(false);
        }
      );
    } else {
      alert('La géolocalisation n\'est pas supportée par votre navigateur.');
      setIsLocating(false);
    }
  };

  useEffect(() => {
    if (isMapLoaded) {
      const adjustedMarkers = adjustMarkerPositions(rinks);
      setMarkers(adjustedMarkers);
    }
  }, [isMapLoaded, rinks]);

  const filteredRinks = getFilteredRinks();

  // Handle selecting a rink from search
  const handleRinkSelect = (rink) => {
    setSelectedRink(rink);
    setMapCenter(rink.location);
    if (mapRef) {
      mapRef.panTo(rink.location);
      mapRef.setZoom(16); // Close zoom for specific rink
    }
  };

  return (
    <div className="rink-map">
      <LoadScript googleMapsApiKey={apiKey}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
          options={{
            disableDefaultUI: true,
            gestureHandling: 'greedy',
            styles: theme === 'dark' ? darkMapStyles : mapStyles,
            clickableIcons: false, // Disable POI clicking
          }}
          onLoad={onMapLoad}
          onClick={() => setSelectedRink(null)}
        >
          {isMapLoaded && filteredRinks.map(rink => {
            const isOpen = rink.ouvert == 1;
            const markerIcon = viewType === 'trail'
              ? createSkiMarkerIcon(isOpen, rink.condition)
              : createRinkMarkerIcon(isOpen, rink.condition);

            return (
              <Marker
                key={rink.nom}
                position={rink.location}
                onClick={() => {
                  setMapCenter(rink.location);
                  setSelectedRink(rink);
                }}
                icon={{
                  url: markerIcon,
                  scaledSize: new window.google.maps.Size(44, 52),
                  anchor: new window.google.maps.Point(22, 52)
                }}
                animation={selectedRink?.nom === rink.nom ?
                  window.google.maps.Animation.BOUNCE : null}
              />
            );
          })}

          {userLocation && (
            <Marker
              position={userLocation}
              icon={{
                url: createUserLocationIcon(),
                scaledSize: new window.google.maps.Size(24, 24),
                anchor: new window.google.maps.Point(12, 12),
              }}
              zIndex={1000}
            />
          )}
        </GoogleMap>
      </LoadScript>

      {/* Map controls overlay */}
      <MapControls
        filter={filter}
        onFilterChange={setFilter}
        rinks={rinks}
        onLocateUser={handleLocateUser}
        onRinkSelect={handleRinkSelect}
        isLocating={isLocating}
        theme={theme}
        toggleTheme={toggleTheme}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        viewType={viewType}
        onViewTypeChange={onViewTypeChange}
      />

      {/* Selected rink card */}
      {selectedRink && (
        <RinkCard
          rink={selectedRink}
          onClose={() => setSelectedRink(null)}
        />
      )}
    </div>
  );
};

export default RinkMap;
